/**
 * useTrainingSessionToken — Batch 18-F7
 *
 * Thin client wrapper around the mint-training-session-token Edge
 * Function. The Edge Function is the authority boundary — it
 * authenticates the caller, validates session ownership, checks
 * session state, generates the token, hashes it, and stores ONLY the
 * hash + expiry on training_consumption_sessions. The plaintext
 * token is returned in the 200 response body exactly once.
 *
 * This hook performs ZERO direct DB writes. It cannot bypass the
 * Edge Function's checks.
 *
 * As of Batch 18-F9B, useOpenTrainingWithSession imports this hook
 * and calls mutateAsync({ sessionId }) on a best-effort basis after a
 * training session is successfully created — see that hook for the
 * launch → mint integration contract. This hook itself remains a
 * thin, side-effect-free wrapper: the Edge Function is still the
 * sole authority boundary, and the plaintext token is still returned
 * exactly once in the mutation result.
 *
 * This hook does NOT:
 *   - write to training_consumption_sessions directly
 *   - write to training_session_extension_evidence directly
 *   - emit training_effectiveness_events
 *   - persist the token to localStorage / sessionStorage
 *   - postMessage the token to any window
 *   - communicate with any browser extension
 *   - reference completion_status / completion_confidence
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MintTrainingSessionTokenInput {
  sessionId: string;
  // K108-Y: on RESUME, allow the owner to rotate the still-active token for this
  // same pending session (the edge function relaxes only its 409 unexpired-token
  // guard; ownership/consent/mintable checks are unchanged).
  resume?: boolean;
}

export interface MintTrainingSessionTokenResult {
  token: string;
  expiresAt: string;
  sessionId: string;
}

export function useTrainingSessionToken() {
  const queryClient = useQueryClient();

  return useMutation<
    MintTrainingSessionTokenResult,
    Error,
    MintTrainingSessionTokenInput
  >({
    mutationFn: async ({ sessionId, resume }) => {
      const { data, error } = await supabase.functions.invoke(
        'mint-training-session-token',
        { body: resume ? { sessionId, resume: true } : { sessionId } },
      );
      if (error) throw error;
      return data as MintTrainingSessionTokenResult;
    },
    onSuccess: (_result, variables) => {
      // Invalidate any in-flight session queries so a UI reading
      // session_token_expires_at picks up the new value. The
      // plaintext token is NOT cached here — callers must use it
      // immediately from the mutation result.
      queryClient.invalidateQueries({
        queryKey: ['consumption-sessions', variables.sessionId],
      });
    },
  });
}
